import { useGeoLocation } from 'hooks/useGeoLocation';
import ChevronDown from 'svg/ChevronDown';
import CloseIcon from 'svg/CloseIcon';
import SearchIcon from 'svg/SearchIcon';
import Button, { ButtonText } from 'components/Button';
import RequestLocationPrompt from 'components/Location/RequestLocationPrompt';
import AddressSearch from 'components/forms/AddressSearch';
import classNames from 'styles/utils/classNames';

const AddressBar: React.FC<{
  onSearch?: () => void;
  onAddressChange?: (coords: { latitude: number; longitude: number }) => void;
  shadowClassName?: string;
  className?: string;
  isInNav?: boolean;
  /** Overrides the pin icon sizing (defaults follow the isInNav variant). */
  iconClassName?: string;
  /** Overrides the variant-driven input typography/spacing classes. */
  inputClassName?: string;
  /** Hides the mobile Search button rendered by the non-nav variant. */
  hideSearchButton?: boolean;
  /** Replaces the search input with static text (e.g. "At your selected venues"). */
  displayOverride?: string;
  /**
   * `bar` (default) is the rounded, shadowed search pill. `field` is the flat
   * form input the Filters modal uses — grey fill, 6px radius, trailing chevron.
   */
  variant?: 'bar' | 'field';
}> = ({
  onSearch,
  onAddressChange,
  shadowClassName,
  className,
  isInNav,
  iconClassName,
  inputClassName,
  hideSearchButton,
  displayOverride,
  variant = 'bar',
}) => {
  const {
    addressString,
    setAddressString,
    suggestedAddresses,
    activeAutocompleteAddress,
    handleSubmitAutocomplete,
    handleAutcompleteSuggestions,
    isLoadingAutocomplete,
    autocompleteInputLength,
    autocompleteMinLength,
  } = useGeoLocation();

  const handleSubmit = async (address?: google.maps.places.AutocompletePrediction | null) => {
    const position = await handleSubmitAutocomplete(address);
    if (address && position && onAddressChange) {
      onAddressChange({ latitude: position.latitude, longitude: position.longitude });
    }
    if (address && onSearch) {
      onSearch();
    }
  };

  const handleClearSearch = () => {
    setAddressString('');
  };

  const isField = variant === 'field';
  // The options are positioned inside AddressSearch, which is inset by the padding,
  // icon and gap on each side (16 + 20 + 4 = 40px); negative insets span the field.
  const suggestionClass = isField ? '-left-10 -right-10 w-auto' : 'w-[17rem] max-md:w-full';
  const shadowClass = isField
    ? shadowClassName
    : shadowClassName ??
      'max-md:shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)]';

  const buttonShadowClass = 'shadow-[0px_4px_20px_0px_rgba(255,92,51,0.50)]';

  return (
    <RequestLocationPrompt position="bottom-left" wrapperClassName={className}>
      <div
        className={classNames(
          'relative flex h-full select-none items-center border-color-border-hover-lightmode dark:border-color-border-hover-darkmode',
          isField
            ? 'gap-ds-xs rounded-md bg-color-bg-lightmode-secondary px-ds-lg dark:bg-color-bg-darkmode-secondary'
            : 'rounded-full bg-color-bg-lightmode-primary px-4 dark:bg-color-bg-darkmode-primary max-md:pr-0 md:py-3',
          shadowClass,
          !isField && (isInNav ? 'gap-0.5' : 'gap-1'),
        )}
      >
        <img
          src="/images/app/pin-gradient.svg"
          alt=""
          className={classNames('shrink-0', iconClassName ?? (isInNav ? 'h-4 w-4' : 'h-5 w-5'))}
        />
        {displayOverride ? (
          <span
            className={classNames(
              'typography-product-subheading w-full truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
              inputClassName,
            )}
          >
            {displayOverride}
          </span>
        ) : (
          <AddressSearch
            handleSubmit={handleSubmit}
            handleAutcompleteSuggestions={handleAutcompleteSuggestions}
            addressString={addressString}
            suggestedAddresses={suggestedAddresses}
            activeAutocompleteAddress={activeAutocompleteAddress}
            isLoadingAutocomplete={isLoadingAutocomplete}
            autocompleteInputLength={autocompleteInputLength}
            autocompleteMinLength={autocompleteMinLength}
            className={classNames(
              'input-unstyled w-full border-none bg-transparent py-0 pl-0 outline-none outline-0 outline-transparent ring-transparent focus:outline-none focus:outline-0 active:outline-0 max-md:pr-0',
              inputClassName ??
                (isField
                  ? 'typography-product-button-label-medium'
                  : isInNav
                  ? 'typography-product-subheading origin-left scale-[0.875] pr-1 max-md:pl-1'
                  : 'typography-product-subheading max-md:pr-8'),
            )}
            placeholder="Search by location"
            suggestionContainerClassName={suggestionClass}
          />
        )}
        {isField && (
          <ChevronDown className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
        )}
        {addressString && !displayOverride && !isField && (
          <ButtonText
            type="button"
            onClick={handleClearSearch}
            className={classNames(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-color-text-lightmode-placeholder transition-all hover:bg-color-bg-lightmode-highlighted focus:outline-none dark:text-color-text-darkmode-placeholder dark:hover:bg-color-bg-lightmode-invert-highlighted max-md:right-[6rem]',
              isInNav ? 'max-lg:right-4 max-lg:px-0 max-md:right-4' : '',
            )}
            aria-label="Clear search"
          >
            <CloseIcon className="h-4 w-4" />
          </ButtonText>
        )}
        {!isInNav && !hideSearchButton && (
          <Button
            variant="brand"
            sizeDesktop="lg"
            sizeMobile="md"
            onClick={() => onSearch?.()}
            className={classNames('w-fit md:hidden', buttonShadowClass)}
          >
            Search
          </Button>
        )}
      </div>
    </RequestLocationPrompt>
  );
};

export default AddressBar;
